import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import {
  HomeIcon,
  UserIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  ArrowRightEndOnRectangleIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import Sidebar, { SidebarSection } from "./Sidebar";
import Header from "./Header";
import { usePermissions } from "../../hooks/usePermissions";

const NAV_STORAGE_KEY = "adminSidebarCollapsed";

/**
 * SuperAdmin portal shell (#148). Renders the same token-styled Sidebar as
 * the workspace via nav-config props — the old slate-styled AdminSidebar
 * copy is gone, so the next redesign can't skip this surface again.
 */
const AdminLayout: React.FC = () => {
  const { hasOrgContext } = usePermissions();

  const [collapsed, setCollapsed] = useState<boolean>(
    () =>
      typeof window !== "undefined" &&
      window.localStorage.getItem(NAV_STORAGE_KEY) === "true",
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(NAV_STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  const sections: SidebarSection[] = [
    {
      label: "Admin Menu",
      items: [
        { label: "Dashboard", icon: HomeIcon, href: "/admin" },
        {
          label: "Organizations",
          icon: BuildingOfficeIcon,
          href: "/admin/organizations",
        },
        {
          label: "Email Testing",
          icon: EnvelopeIcon,
          href: "/admin/email-testing",
        },
        { label: "Strategies", icon: Cog6ToothIcon, href: "/admin/strategies" },
        { label: "Profile", icon: UserIcon, href: "/admin/profile" },
      ],
    },
    ...(hasOrgContext
      ? [
          {
            label: "Organization",
            items: [
              {
                label: "Switch to Org View",
                icon: ArrowRightEndOnRectangleIcon,
                href: "/",
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-canvas text-text-base">
      <Sidebar
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        sections={sections}
        badgeText="Admin"
        brandTo="/admin"
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((c) => !c)}
          onOpenMobile={() => setMobileOpen(true)}
        />
        <main className="min-h-0 flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
